import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import Button from "@material-ui/core/Button";
import CloseIcon from "@material-ui/icons/Close";
import CircularProgress from "@material-ui/core/CircularProgress";
import Grid from "@material-ui/core/Grid";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import CheckIcon from "@material-ui/icons/Check";
import InputAdornment from "@material-ui/core/InputAdornment";
import TextField from "@material-ui/core/TextField";

import {
  fetchVerifyVpaParams,
  submitUpiMerchantCollectRequest,
  submitSimulateCallback
} from "../../../actions/upi";
import { FormatAmount } from "../../../utils/Formatter";

import "./UpiForm.css";

const isFormValid = form => {
  if (!form.vpa) return { errorMsg: "Please enter a VPA" };
  if (!/^\w+[-_.]*\w+@\w+$/.test(form.vpa)) {
    return { errorMsg: "Please enter a valid VPA" };
  }
  return { errorMsg: undefined };
};

export const UpiForm = ({
  charge,
  dispatch,
  isLoading,
  isPolling,
  errorMsg,
  vpa,
  customerName,
  trsUuid,
  payapp
}) => {
  const { uuid, amount, currency, testMode } = charge;
  const [form, setForm] = useState({ vpa, customerName });
  const [validation, setValidation] = useState({ errorMsg });

  useEffect(() => {
    setForm(prevForm => ({ ...prevForm, customerName }));
  }, [customerName]);

  const validateForm = () => {
    const validationResult = isFormValid(form);
    setValidation(validationResult);
    return validationResult;
  };

  const submitValidateVpaRequest = e => {
    if (e) e.preventDefault();
    const validationResult = validateForm();
    if (validationResult.errorMsg) {
      setValidation(validationResult);
    } else {
      dispatch(fetchVerifyVpaParams(uuid, form));
    }
  };

  const submitMerchantCollectRequest = () => {
    dispatch(submitUpiMerchantCollectRequest(uuid, form));
  };

  const isVpaValid = vpa && vpa === form.vpa;

  return (
    <form className="upi-form" onSubmit={submitValidateVpaRequest}>
      <Grid container justify="center" spacing={2}>
        <Grid item sm={8} xs={12}>
          <TextField
            id="vpa"
            label="VPA"
            placeholder="xxx@yyy"
            disabled={isLoading}
            value={form.vpa}
            onChange={e => {
              const { value } = e.target;
              setForm({ ...form, vpa: value });
              setValidation({ errorMsg: undefined });
            }}
            onBlur={validateForm}
            error={!!(errorMsg || validation.errorMsg)}
            helperText={
              errorMsg || validation.errorMsg || (isVpaValid && customerName)
            }
            type="text"
            className="upi-vpa"
            fullWidth
            autoFocus
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {!isVpaValid && <HelpOutlineIcon color="disabled" />}
                  {isVpaValid && (
                    <CheckIcon color={isPolling ? "disabled" : "primary"} />
                  )}
                </InputAdornment>
              )
            }}
          />
        </Grid>
        <Grid item sm={4} xs={12}>
          <Button
            fullWidth
            id="verify-button"
            variant="outlined"
            color="primary"
            onClick={submitValidateVpaRequest}
            disabled={!!(isLoading || isVpaValid)}
          >
            {isLoading && !isVpaValid ? (
              <CircularProgress size={28} />
            ) : (
              "Verify"
            )}
          </Button>
        </Grid>
        {isPolling && (
          <Grid item xs={12} id="upi-polling-indicator">
            <CircularProgress color="secondary" />
            <p>Please accept the payment request on your UPI app.</p>
          </Grid>
        )}
        {!isPolling && (
          <Grid item xs={12} style={{ textAlign: "center" }}>
            <Button
              id="pay-button"
              variant="outlined"
              color="primary"
              onClick={submitMerchantCollectRequest}
              disabled={!vpa || isLoading || vpa !== form.vpa}
            >
              Pay&nbsp;
              {FormatAmount(amount, currency)}
            </Button>
          </Grid>
        )}
        {isPolling && testMode && (
          <Grid item xs={12} style={{ textAlign: "center" }}>
            <Button
              id="simulate-ok-button"
              onClick={() => submitSimulateCallback(true, trsUuid, payapp)}
            >
              <CheckIcon />
              &nbsp;Simulate success
            </Button>
            <Button
              id="simulate-ko-button"
              onClick={() => submitSimulateCallback(false, trsUuid, payapp)}
            >
              <CloseIcon />
              &nbsp;Simulate failure
            </Button>
          </Grid>
        )}
      </Grid>
    </form>
  );
};

UpiForm.propTypes = {
  charge: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    amount: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired,
    testMode: PropTypes.bool.isRequired
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isPolling: PropTypes.bool.isRequired,
  vpa: PropTypes.string.isRequired,
  errorMsg: PropTypes.string,
  customerName: PropTypes.string,
  trsUuid: PropTypes.string,
  payapp: PropTypes.string
};

UpiForm.defaultProps = {
  errorMsg: undefined,
  customerName: undefined,
  trsUuid: undefined,
  payapp: undefined
};

function mapStateToProps({ upiReducer }) {
  return upiReducer;
}

export default connect(mapStateToProps)(UpiForm);
